import { computed, ref, unref, watch } from 'vue';

/**
 * Preferencia que pinta na hora e so espera a rede para gravar.
 * Se a gravacao falhar (rejeitar ou devolver false), a tela volta.
 */
export function useOptimisticSetting(source, persist) {
  const override = ref(undefined);
  const hasOverride = ref(false);

  const value = computed(() =>
    hasOverride.value ? override.value : unref(source)
  );

  watch(
    () => unref(source),
    next => {
      if (!hasOverride.value) return;
      if (Object.is(next, override.value)) {
        hasOverride.value = false;
        override.value = undefined;
      }
    }
  );

  const set = async next => {
    const previous = value.value;
    override.value = next;
    hasOverride.value = true;
    try {
      const result = await persist(next);
      if (result === false) {
        override.value = previous;
        hasOverride.value = true;
        return false;
      }
      return true;
    } catch {
      override.value = previous;
      hasOverride.value = true;
      return false;
    }
  };

  return { value, set };
}

// Overlay compartilhado: todos os callers de useWorkbenchLayout leem o mesmo
// estado otimista. Sem isso, um clique no cluster e a largura da lista
// viveriam em overlays diferentes.
const sharedOverlay = ref({});

export function resetOptimisticSettings() {
  sharedOverlay.value = {};
}

const hasOwn = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj || {}, key);

/**
 * Overlay de varias chaves em cima de um objeto persistido (uiSettings).
 * Mesma regra: pinta na hora, reverte o lote se a gravacao falhar.
 */
export function useOptimisticPatch(source, persist) {
  const value = computed(() => ({
    ...(unref(source) || {}),
    ...sharedOverlay.value,
  }));

  watch(
    () => unref(source),
    next => {
      const keys = Object.keys(sharedOverlay.value);
      if (!keys.length) return;
      const nextOverlay = { ...sharedOverlay.value };
      let changed = false;
      keys.forEach(key => {
        if (Object.is(next?.[key], nextOverlay[key])) {
          delete nextOverlay[key];
          changed = true;
        }
      });
      if (changed) sharedOverlay.value = nextOverlay;
    }
  );

  const commit = async patch => {
    const snapshot = {};
    Object.keys(patch).forEach(key => {
      snapshot[key] = hasOwn(sharedOverlay.value, key)
        ? sharedOverlay.value[key]
        : unref(source)?.[key];
    });
    sharedOverlay.value = { ...sharedOverlay.value, ...patch };
    try {
      const result = await persist(patch);
      if (result === false) {
        sharedOverlay.value = { ...sharedOverlay.value, ...snapshot };
        return false;
      }
      return true;
    } catch {
      sharedOverlay.value = { ...sharedOverlay.value, ...snapshot };
      return false;
    }
  };

  return { value, commit };
}

export function useOptimisticSettings(uiSettings, persist) {
  const patch = useOptimisticPatch(uiSettings, persist);
  return {
    uiSettings: patch.value,
    updateUISettings: patch.commit,
  };
}
